import React, { useState } from "react";
import { Link } from "react-router-dom";
import LegalPage, { Section, Well, H3, P } from "./components/LegalPage";
import { brand as ds } from "./designSystem";

const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://schriftbot-backend.vercel.app";

/* ════════════════════════════════════════════════════════════════════════
   § 356a BGB — Widerrufsbutton (Pflicht seit 19.06.2026).
   ────────────────────────────────────────────────────────────────────────
   Die Norm schreibt zwei Schritte vor, nicht einen:

     1. eine ständig verfügbare Schaltfläche „Vertrag widerrufen“,
     2. eine Bestätigungsseite mit den Eingabefeldern, die mit einer
        Schaltfläche „Widerruf bestätigen“ abgeschlossen wird.

   Vorher war beides auf einer Seite zusammengefallen und der Absende-Button
   trug die Beschriftung des Einstiegs-Buttons. Jetzt läuft es als echte
   zwei Schritte, und der Eingang wird mit Referenz, Datum und Uhrzeit auf
   einem dauerhaften Datenträger bestätigt.

   Der Ton ist bewusst neutral: Widerrufen ist ein Recht, kein Problemfall.
   Der frühere rote Warnkasten ist ein sachlicher Hinweis geworden.
   ════════════════════════════════════════════════════════════════════════ */
/* Modul-Ebene, nicht in der Komponente: eine im Render-Body definierte
   Komponente bekommt bei jedem State-Update eine neue Identität, React
   hängt den Teilbaum neu ein — und das Eingabefeld verliert bei jedem
   Tastendruck den Fokus. */
const Field = ({ label, hint, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <span
      style={{
        fontFamily: ds.font.body,
        fontSize: 14,
        fontWeight: 600,
        color: ds.color.ink,
      }}
    >
      {label}
    </span>
    {hint && (
      <span
        style={{
          fontFamily: ds.font.body,
          fontSize: 14,
          color: ds.color.muted,
        }}
      >
        {hint}
      </span>
    )}
    {children}
  </label>
);

export default function Widerruf() {
  const [step, setStep] = useState("start"); // start | form | done
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [orderInfo, setOrderInfo] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/withdraw-contract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, orderInfo, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Fehler ${res.status}`);
      setResult(data);
      setStep("done");
      setStatus("idle");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("de-DE");
    } catch {
      return iso;
    }
  };

  /* ── Schritt 3: Eingangsbestätigung ── */
  if (step === "done") {
    return (
      <LegalPage
        title="Widerruf eingegangen"
        intro="Wir haben deine Widerrufserklärung erhalten und bestätigen hiermit den Eingang."
      >
        <Section>
          {/* § 356a Abs. 4 BGB: Inhalt der Erklärung, Datum und Uhrzeit. */}
          <Well style={{ marginBottom: 16 }}>
            <P style={{ margin: 0 }}>
              Referenznummer:{" "}
              <strong>{result?.reference || "—"}</strong>
              <br />
              Eingang: {fmtDate(result?.receivedAt) || "—"}
              <br />
              Name: {name}
              <br />
              E-Mail-Adresse: {email}
              {orderInfo && (
                <>
                  <br />
                  Vertrag: {orderInfo}
                </>
              )}
              {message && (
                <>
                  <br />
                  Nachricht: {message}
                </>
              )}
              <br />
              Erklärung: Hiermit widerrufe ich den oben bezeichneten Vertrag.
            </P>
          </Well>
          <P muted>
            Bitte bewahre oder drucke diese Bestätigung zu deinen Unterlagen
            aus. Eine Kopie schicken wir dir zusätzlich per E-Mail.
          </P>
          <P muted style={{ margin: 0 }}>
            Zahlungen erstatten wir unverzüglich, spätestens binnen vierzehn
            Tagen ab Eingang dieser Erklärung, über das Zahlungsmittel, mit dem
            du bezahlt hast.
          </P>
          <div style={{ marginTop: 24 }}>
            <Link to="/" className="sb-btn sb-btn-primary">
              Zurück zur Startseite
            </Link>
          </div>
        </Section>
      </LegalPage>
    );
  }

  /* ── Schritt 1: die Widerrufsschaltfläche ── */
  if (step === "start") {
    return (
      <LegalPage
        title="Vertrag widerrufen"
        intro="Du kannst deinen Vertrag binnen 14 Tagen ohne Angabe von Gründen widerrufen — ohne Anmeldung, direkt hier."
      >
        <Section>
          <P>
            Der nächste Schritt öffnet ein kurzes Formular. Dort trägst du die
            Daten deiner Bestellung ein und schließt den Widerruf mit
            „Widerruf bestätigen“ ab. Den Eingang bestätigen wir dir sofort mit
            einer Referenznummer.
          </P>
          {/* § 356a BGB: die Schaltfläche trägt genau diese Beschriftung. */}
          <button
            onClick={() => setStep("form")}
            className="sb-btn sb-btn-primary"
            style={{ marginTop: 10 }}
          >
            Vertrag widerrufen
          </button>
        </Section>

        <Section>
          <H3>Widerruf oder Kündigung</H3>
          <P muted style={{ margin: 0 }}>
            Du willst dein Abo nur beenden, nicht widerrufen? Dann ist die{" "}
            <Link to="/kuendigen" className="sb-link">
              Kündigung
            </Link>{" "}
            der richtige Weg — sie gilt zum Ende des laufenden
            Abrechnungsmonats. Die vollständige{" "}
            <Link to="/agb#widerruf" className="sb-link">
              Widerrufsbelehrung
            </Link>{" "}
            steht in den AGB.
          </P>
        </Section>
      </LegalPage>
    );
  }

  /* ── Schritt 2: Bestätigungsseite ── */
  return (
    <LegalPage
      title="Widerruf bestätigen"
      intro="Trage die Daten deiner Bestellung ein und schließe den Widerruf ab."
    >
      <Section>
        <form onSubmit={handleWithdraw}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Field
              label="E-Mail-Adresse deiner Bestellung"
              hint="An diese Adresse senden wir die Eingangsbestätigung."
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sb-input"
                placeholder="deine@email.de"
              />
            </Field>

            <Field label="Name">
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="sb-input"
              />
            </Field>

            <Field
              label="Welchen Vertrag widerrufst du?"
              hint="Optional, zum Beispiel Tarif und Bestelldatum. Die E-Mail-Adresse genügt zur Zuordnung."
            >
              <input
                type="text"
                value={orderInfo}
                onChange={(e) => setOrderInfo(e.target.value)}
                className="sb-input"
              />
            </Field>

            <Field label="Nachricht" hint="Optional">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                className="sb-input"
                style={{ resize: "vertical" }}
              />
            </Field>
          </div>

          {status === "error" && (
            <P style={{ color: ds.color.danger, marginTop: 16 }}>
              Das hat nicht geklappt: {error}. Du kannst deinen Widerruf auch
              formlos an schriftbot@gmail.com senden — die Frist wahrst du
              damit genauso.
            </P>
          )}

          {/* § 356a BGB: die Bestätigungsschaltfläche. */}
          <button
            type="submit"
            disabled={status === "loading"}
            className="sb-btn sb-btn-primary"
            style={{ width: "100%", marginTop: 24 }}
          >
            {status === "loading" ? "Wird gesendet …" : "Widerruf bestätigen"}
          </button>

          <button
            type="button"
            onClick={() => setStep("start")}
            className="sb-btn sb-btn-secondary"
            style={{ width: "100%", marginTop: 10 }}
          >
            Zurück
          </button>
        </form>
      </Section>
    </LegalPage>
  );
}
